import { useState, useEffect, useCallback } from 'react';
import { Pencil, Plus, Check, X, UploadCloud, Download } from 'lucide-react';
import { API_BASE } from '../config';
import { FONT } from '../dmsTheme';
import { exportSheet, todayStamp } from '../excel';
import { notifyEditBlocked } from './EditAccessToast';

/* ============================================================================
 * DistributorSkuMasterTable — shared display+edit view into the single
 * `dms_sku_master` table, scoped per distributor via `nameCol`. Used
 * identically by Uprising/Fatehpuria/Choudhary's SKU Master tab (generalized
 * from Fatehpuria's original AdminTable).
 *
 * Props:
 *   distributorKey — 'uprising' | 'fatehpuria' | 'choudhary' (drives which
 *     dms.js-family endpoint's sku_master/upsert actions are called)
 *   nameCol         — the dms_sku_master column this distributor edits
 *                     (e.g. 'uprising_name', 'choudhary_name', 'fatehpuria_name')
 *   nameLabel       — display label for that column (default: Title-cased nameCol)
 *   extraCols       — optional [{ key, label }] for Fatehpuria's 4 extra columns
 *   canEdit         — whether the current user may write
 *   endpoint        — base API path, e.g. '/dms', '/fatehpuria-inventory',
 *                     '/choudhary-inventory' (each exposes ?action=sku_master
 *                     GET + ?action=upsert_sku_master POST, scoped by name_col)
 *   updatedBy       — username for audit columns
 * ========================================================================== */

export default function DistributorSkuMasterTable({ nameCol, nameLabel, extraCols = [], canEdit, endpoint, updatedBy }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [view, setView] = useState('all');
  const [unmapped, setUnmapped] = useState([]);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedEans, setUnmappedEans] = useState({});
  const [savingName, setSavingName] = useState(null);

  const columns = [
    { key: 'ean', label: 'EAN', width: '1fr' },
    { key: nameCol, label: nameLabel || nameCol, width: '2fr' },
    ...extraCols.map((c) => ({ ...c, width: c.width || '1.6fr' })),
  ];

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}${endpoint}?action=sku_master&name_col=${encodeURIComponent(nameCol)}&search=${encodeURIComponent(q)}`);
      const body = await res.json();
      setRows(Array.isArray(body) ? body : (Array.isArray(body?.rows) ? body.rows : []));
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false); }
  }, [endpoint, nameCol]);

  useEffect(() => { load(); }, [load]);

  const loadUnmapped = useCallback(async () => {
    setUnmappedLoading(true);
    try {
      const res = await fetch(`${API_BASE}${endpoint}?action=unmapped_skus`);
      const body = await res.json();
      setUnmapped(Array.isArray(body) ? body : []);
    } catch (e) { /* non-fatal */ }
    finally { setUnmappedLoading(false); }
  }, [endpoint]);

  useEffect(() => { loadUnmapped(); }, [loadUnmapped]);

  async function saveUnmappedEan(name) {
    if (!canEdit) return notifyEditBlocked();
    const ean = (unmappedEans[name] || '').trim();
    if (!ean) return;
    setSavingName(name);
    try {
      const res = await fetch(`${API_BASE}${endpoint}?action=upsert_sku_master`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ean, [nameCol]: name, updated_by: updatedBy || 'Portal' }),
      });
      const body = await res.json();
      if (!res.ok || body?.success === false) throw new Error(body?.error || 'Save failed');
      setUnmapped((u) => u.filter((n) => n !== name));
      load(search);
    } catch (e) { setMsg({ type: 'error', text: e.message || 'Save failed' }); }
    finally { setSavingName(null); }
  }

  function downloadUnmapped() {
    exportSheet({
      headers: ['EAN', nameLabel || nameCol],
      rows: unmapped.map((n) => [null, n]),
      sheetName: 'Unmapped',
      filename: `unmapped-${nameCol}-${todayStamp()}.xlsx`,
    });
  }

  function downloadSampleFormat() {
    exportSheet({
      headers: ['EAN', nameLabel || nameCol],
      rows: [['8904134000000', 'Example Product Name']],
      sheetName: 'Sample',
      filename: 'sku-mapping-sample-format.xlsx',
    });
  }

  const template = { ean: '', [nameCol]: '', ...Object.fromEntries(extraCols.map((c) => [c.key, ''])) };

  function startEdit(row) {
    if (!canEdit) return notifyEditBlocked();
    setEditingId(row.id); setDraft({ ...row }); setErr(null);
  }
  function startAdd() {
    if (!canEdit) return notifyEditBlocked();
    setAdding(true); setDraft({ ...template }); setErr(null);
  }
  function cancel() { setEditingId(null); setAdding(false); setDraft({}); setErr(null); }

  async function save() {
    setSaving(true); setErr(null);
    try {
      const res = await fetch(`${API_BASE}${endpoint}?action=upsert_sku_master`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, updated_by: updatedBy || 'Portal' }),
      });
      const body = await res.json();
      if (!res.ok || body?.success === false) throw new Error(body?.error || 'Save failed');
      cancel();
      setMsg({ type: 'success', text: 'Saved' });
      load(search);
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function handleBulkUpload(e) {
    if (!canEdit) return notifyEditBlocked();
    const file = e.target.files[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}${endpoint}?action=bulk_upload_sku_master&name_col=${encodeURIComponent(nameCol)}&updated_by=${encodeURIComponent(updatedBy || 'Portal')}`, { method: 'POST', body: form });
      const body = await res.json();
      if (res.ok && body.success) {
        setMsg({ type: 'success', text: `${body.inserted || 0} added, ${body.updated || 0} updated` });
        load(search);
      } else {
        setMsg({ type: 'error', text: body?.error || 'Upload failed' });
      }
    } catch { setMsg({ type: 'error', text: 'Network error' }); }
    finally { e.target.value = ''; }
  }

  const gridTemplateColumns = columns.map((c) => c.width).join(' ') + ' 90px';
  const T = { ink: 'var(--grn-text)', border: 'var(--grn-border)', muted: 'var(--grn-text-muted)', purpleTint: 'var(--grn-purple-tint)', purple: 'var(--grn-purple)', surfaceSubtle: 'var(--grn-surface-subtle)', green: 'var(--grn-green)', error: 'var(--grn-error)' };

  return (
    <div style={{ background: 'var(--grn-surface)', borderRadius: 16, padding: '18px 20px', boxShadow: 'var(--grn-shadow-sm)', border: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>SKU Master ({rows.length})</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
            {canEdit ? 'You have edit access — add, edit, or bulk-upload mappings below.' : 'View only — ask an admin for edit access to add or change mappings.'}
            {' '}Any unmapped row stays in your data as-is; map it here whenever convenient.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {view === 'all' && (
            <input value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value); }} placeholder="Search EAN or name..."
              style={{ padding: '8px 11px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surfaceSubtle, fontFamily: FONT, fontSize: 12.5, minWidth: 220 }} />
          )}
          {canEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.purpleTint, color: T.purple, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <UploadCloud size={13} /> Bulk upload
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleBulkUpload} />
            </label>
          )}
          <button onClick={downloadSampleFormat} style={{ background: 'none', border: 'none', color: T.purple, fontSize: 11.5, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Download Sample Format
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[{ key: 'all', label: 'All' }, { key: 'unmapped', label: `Unmapped (${unmapped.length})` }].map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} style={{
            fontSize: 11.5, fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
            background: view === v.key ? T.purple : T.surfaceSubtle, color: view === v.key ? '#fff' : T.muted, border: `1px solid ${view === v.key ? T.purple : T.border}`,
          }}>
            {v.label}
          </button>
        ))}
      </div>

      {msg && <div style={{ fontSize: 11, color: msg.type === 'error' ? T.error : T.green, marginBottom: 10 }}>{msg.text}</div>}

      {view === 'unmapped' ? (
        <div style={{ overflow: 'auto' }}>
          {unmapped.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <button onClick={downloadUnmapped} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.purpleTint, color: T.purple, border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Download size={13} /> Download Unmapped (Excel)
              </button>
            </div>
          )}
          <div style={{ minWidth: 500 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 90px', background: 'var(--grn-accent-surface)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
              {[nameLabel || nameCol, 'EAN', ''].map((h, i) => <div key={i} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>{h}</div>)}
            </div>
            {unmappedLoading && <div style={{ padding: 30, textAlign: 'center', color: T.muted }}>Loading…</div>}
            {!unmappedLoading && unmapped.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>Nothing unmapped — every SKU has an EAN.</div>}
            {unmapped.map((name, i) => (
              <div key={name} style={{ display: 'grid', gridTemplateColumns: '2.5fr 1fr 90px', background: i % 2 ? T.surfaceSubtle : 'var(--grn-surface)', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ padding: '9px 13px', fontSize: 12.5, color: T.ink }}>{name}</div>
                <div style={{ padding: '7px 9px' }}>
                  <input value={unmappedEans[name] || ''} onChange={(e) => setUnmappedEans((u) => ({ ...u, [name]: e.target.value }))}
                    placeholder="Enter EAN" disabled={!canEdit}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${T.border}`, fontFamily: FONT, fontSize: 12 }} />
                </div>
                <div style={{ padding: '7px 9px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => saveUnmappedEan(name)} disabled={!canEdit || savingName === name || !(unmappedEans[name] || '').trim()}
                    style={{ background: T.green, color: '#fff', border: 'none', borderRadius: 6, padding: 5, cursor: 'pointer' }} title="Save">
                    <Check size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div style={{ overflow: 'auto' }}>
        <div style={{ minWidth: columns.length * 160 }}>
          <div style={{ display: 'grid', gridTemplateColumns, background: 'var(--grn-accent-surface)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
            {columns.map((c) => <div key={c.key} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>{c.label}</div>)}
            <div style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', textAlign: 'right' }}>
              {canEdit && (
                <button onClick={startAdd} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  <Plus size={11} /> Add
                </button>
              )}
            </div>
          </div>
          {adding && <EditableRow columns={columns} draft={draft} setDraft={setDraft} gridTemplateColumns={gridTemplateColumns} saving={saving} onSave={save} onCancel={cancel} />}
          {loading && <div style={{ padding: 30, textAlign: 'center', color: T.muted }}>Loading…</div>}
          {!loading && rows.length === 0 && !adding && <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>No rows yet.</div>}
          {rows.map((r, i) => (
            editingId === r.id ? (
              <EditableRow key={r.id} columns={columns} draft={draft} setDraft={setDraft} gridTemplateColumns={gridTemplateColumns} saving={saving} onSave={save} onCancel={cancel} />
            ) : (
              <div key={r.id} style={{ display: 'grid', gridTemplateColumns, background: i % 2 ? T.surfaceSubtle : 'var(--grn-surface)', borderBottom: `1px solid ${T.border}` }}>
                {columns.map((c) => (
                  <div key={c.key} style={{ padding: '11px 13px', fontSize: 12.5, color: c.key === 'ean' && !r[c.key] ? T.error : T.ink }}>
                    {r[c.key] ?? '—'}
                  </div>
                ))}
                <div style={{ padding: '9px 13px', textAlign: 'right' }}>
                  <button onClick={() => startEdit(r)} style={{ background: 'none', border: 'none', color: T.purple, cursor: 'pointer', padding: 4 }} title={canEdit ? 'Edit' : 'View only'}>
                    <Pencil size={13} />
                  </button>
                </div>
              </div>
            )
          ))}
          {err && <div style={{ padding: '8px 13px', fontSize: 11.5, color: T.error }}>{err}</div>}
        </div>
      </div>
      )}
    </div>
  );
}

function EditableRow({ columns, draft, setDraft, gridTemplateColumns, saving, onSave, onCancel }) {
  const T = { purpleTint: 'var(--grn-purple-tint)', border: 'var(--grn-border)', green: 'var(--grn-green)', surfaceSubtle: 'var(--grn-surface-subtle)', muted: 'var(--grn-text-muted)' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns, background: T.purpleTint, borderBottom: `1px solid ${T.border}` }}>
      {columns.map((c) => (
        <div key={c.key} style={{ padding: '7px 9px' }}>
          <input
            value={draft[c.key] ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, [c.key]: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${T.border}`, fontFamily: FONT, fontSize: 12 }}
          />
        </div>
      ))}
      <div style={{ padding: '7px 9px', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <button onClick={onSave} disabled={saving} style={{ background: T.green, color: '#fff', border: 'none', borderRadius: 6, padding: 5, cursor: 'pointer' }} title="Save"><Check size={13} /></button>
        <button onClick={onCancel} style={{ background: T.surfaceSubtle, color: T.muted, border: 'none', borderRadius: 6, padding: 5, cursor: 'pointer' }} title="Cancel"><X size={13} /></button>
      </div>
    </div>
  );
}
